import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { TransactionMode, VoucherFormData } from '../../../types/voucher.types';
import { voucherService } from '../../../services/voucher.service';
import { bankAccountService } from '../../../services/bank-account.service';
import { BankAccount } from '../../../types/bank-account.types';
export function ReceiveVoucherCreate() {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saveAction, setSaveAction] = useState<'draft' | 'complete'>('draft');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankName, setSelectedBankName] = useState<string>('');
  const [filteredAccounts, setFilteredAccounts] = useState<BankAccount[]>([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [formData, setFormData] = useState<VoucherFormData>({
    voucherType: 'CRV' as any,
    date: new Date().toISOString().split('T')[0],
    transactionMode: TransactionMode.CASH,
    lines: [{
      accountId: '',
      debit: 0,
      credit: 0,
      description: ''
    }]
  });
  useEffect(() => {
    loadBankAccounts();
  }, []);
  const loadBankAccounts = async () => {
    try {
      const response = await bankAccountService.getBankAccounts({
        status: 'Active' as any
      });
      setBankAccounts(response.data);
      console.log('Loaded bank accounts:', response.data);
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    }
  };
  const handleBankNameChange = (bankName: string) => {
    setSelectedBankName(bankName);
    const filtered = bankAccounts.filter(acc => acc.bankName === bankName);
    setFilteredAccounts(filtered);
    setSelectedBankAccount(null);
    setFormData({
      ...formData,
      bankAccountId: ''
    });
  };
  const handleAccountNoChange = (accountId: string) => {
    const account = filteredAccounts.find(a => a.id === accountId);
    setSelectedBankAccount(account || null);
    setFormData({
      ...formData,
      bankAccountId: accountId
    });
  };
  const uniqueBankNames = Array.from(new Set(bankAccounts.map(acc => acc.bankName)));
  const transactionModes = [{
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const mockAccounts = [{
    value: '1',
    label: '1100 - Cash'
  }, {
    value: '2',
    label: '1200 - Bank Account'
  }, {
    value: '4',
    label: '4000 - Sales Revenue'
  }, {
    value: '5',
    label: '4100 - Service Revenue'
  }];
  const mockParties = [{
    value: 'party1',
    label: 'ABC Trading Company'
  }, {
    value: 'party2',
    label: 'XYZ Suppliers Ltd'
  }];
  const mockCostCenters = [{
    value: 'cc1',
    label: 'Operations'
  }, {
    value: 'cc2',
    label: 'Administration'
  }];
  const mockProjects = [{
    value: 'proj1',
    label: 'Infrastructure Project'
  }, {
    value: 'proj2',
    label: 'Education Initiative'
  }];
  // Check if Bank mode is selected - use string comparison
  const isBankMode = formData.transactionMode === 'Bank' || formData.transactionMode === TransactionMode.BANK;
  console.log('Transaction Mode:', formData.transactionMode);
  console.log('Is Bank Mode:', isBankMode);
  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, {
        accountId: '',
        debit: 0,
        credit: 0,
        description: ''
      }]
    });
  };
  const removeLine = (index: number) => {
    if (formData.lines.length > 1) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index)
      });
    }
  };
  const updateLine = (index: number, field: string, value: any) => {
    const updated = [...formData.lines];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setFormData({
      ...formData,
      lines: updated
    });
  };
  const handleSubmit = async (action: 'draft' | 'complete') => {
    const totalCredit = formData.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
    if (totalCredit === 0) {
      alert('Please enter credit amounts');
      return;
    }
    if (isBankMode && (!selectedBankName || !formData.bankAccountId)) {
      alert('Please select bank name and account number');
      return;
    }
    setSaveAction(action);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    try {
      const voucher = await voucherService.createVoucher(formData);
      if (saveAction === 'complete') {
        await voucherService.completeVoucher(voucher.id);
      }
      navigate('/voucher/receive');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setShowConfirmModal(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1922">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-1923">
        <div className="flex items-center gap-4" data-id="element-1924">
          <Button variant="ghost" onClick={() => navigate('/voucher/receive')} data-id="element-1925">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1926" />
          </Button>
          <div data-id="element-1927">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1928">
              Create Receive Voucher
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1929">
              Record cash or bank receipts
            </p>
          </div>
        </div>

        <form onSubmit={e => {
        e.preventDefault();
        handleSubmit('draft');
      }} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-1930">
          <div data-id="element-1931">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1932">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1933">
              <Input label="Transaction Date" type="date" required value={formData.date} disabled helperText="System current date" data-id="element-1934" />

              <Select label="Transaction Mode" required options={transactionModes} value={formData.transactionMode} onChange={e => {
              console.log('Selected transaction mode:', e.target.value);
              setFormData({
                ...formData,
                transactionMode: e.target.value as any,
                bankAccountId: ''
              });
              setSelectedBankName('');
              setFilteredAccounts([]);
              setSelectedBankAccount(null);
            }} data-id="element-1935" />

              <div data-id="element-1936">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1937">
                  Party
                </label>
                <input list="parties" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={formData.partyName || ''} onChange={e => setFormData({
                ...formData,
                partyName: e.target.value
              })} placeholder="Select or type party name" data-id="element-1938" />
                <datalist id="parties" data-id="element-1939">
                  {mockParties.map(party => <option key={party.value} value={party.label} data-id="element-1940" />)}
                </datalist>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1941">
                  Select from list or type custom name
                </p>
              </div>

              <Input label="Reference Number" value={formData.referenceNo} onChange={e => setFormData({
              ...formData,
              referenceNo: e.target.value
            })} placeholder="Optional reference" data-id="element-1942" />

              <Select label="Cost Center" options={[{
              value: '',
              label: 'Select Cost Center'
            }, ...mockCostCenters]} value={formData.costCenterId} onChange={e => setFormData({
              ...formData,
              costCenterId: e.target.value
            })} data-id="element-1943" />

              <Select label="Project" options={[{
              value: '',
              label: 'Select Project'
            }, ...mockProjects]} value={formData.projectId} onChange={e => setFormData({
              ...formData,
              projectId: e.target.value
            })} data-id="element-1944" />
            </div>
          </div>

          {/* Bank Details Section - Always show for debugging */}
          {isBankMode && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1945">
              <h4 className="text-sm font-semibold text-blue-900 mb-4" data-id="element-1946">
                Bank Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1947">
                <Select label="Bank Name" required options={[{
              value: '',
              label: 'Select Bank Name'
            }, ...uniqueBankNames.map(name => ({
              value: name,
              label: name
            }))]} value={selectedBankName} onChange={e => handleBankNameChange(e.target.value)} helperText="Select bank name first" data-id="element-1948" />

                <Select label="Account No" required options={[{
              value: '',
              label: 'Select Account No'
            }, ...filteredAccounts.map(acc => ({
              value: acc.id,
              label: `${acc.accountNo} - ${acc.branch}`
            }))]} value={formData.bankAccountId} onChange={e => handleAccountNoChange(e.target.value)} disabled={!selectedBankName} helperText={selectedBankName ? 'Select account number' : 'Select bank name first'} data-id="element-1949" />

                {selectedBankAccount && <div className="md:col-span-2" data-id="element-1950">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1951">
                      Branch
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-1952">
                      {selectedBankAccount.branch}
                    </div>
                  </div>}
              </div>
            </div>}

          <div data-id="element-1953">
            <div className="flex items-center justify-between mb-4" data-id="element-1954">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1955">
                Transaction Lines
              </h3>
              <Button type="button" size="sm" onClick={addLine} data-id="element-1956">
                Add Line
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden" data-id="element-1957">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1958">
                <thead className="bg-gray-50" data-id="element-1959">
                  <tr data-id="element-1960">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1961">
                      Account
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1962">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1963">
                      Credit
                    </th>
                    <th className="px-4 py-3 w-16" data-id="element-1964"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1965">
                  {formData.lines.map((line, index) => <tr key={index} data-id="element-1966">
                      <td className="px-4 py-3" data-id="element-1967">
                        <Select options={[{
                      value: '',
                      label: 'Select Account'
                    }, ...mockAccounts]} value={line.accountId} onChange={e => updateLine(index, 'accountId', e.target.value)} className="min-w-[200px]" data-id="element-1968" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1969">
                        <Input value={line.description} onChange={e => updateLine(index, 'description', e.target.value)} placeholder="Description" data-id="element-1970" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1971">
                        <Input type="number" value={line.credit} onChange={e => updateLine(index, 'credit', Number(e.target.value))} className="text-right" min="0" step="0.01" data-id="element-1972" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1973">
                        {formData.lines.length > 1 && <button type="button" onClick={() => removeLine(index)} className="text-red-600 hover:text-red-800" data-id="element-1974">
                            ×
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50" data-id="element-1975">
                  <tr data-id="element-1976">
                    <td colSpan={2} className="px-4 py-3 text-right font-semibold text-gray-900" data-id="element-1977">
                      Total Credit:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900" data-id="element-1978">
                      $
                      {formData.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0).toLocaleString()}
                    </td>
                    <td data-id="element-1979"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1980">
            <div data-id="element-1981">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1982">
                Description
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} placeholder="Enter voucher description..." data-id="element-1983" />
            </div>

            <div data-id="element-1984">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1985">
                Narration
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.narration} onChange={e => setFormData({
              ...formData,
              narration: e.target.value
            })} placeholder="Additional notes..." data-id="element-1986" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-1987">
            <Button type="button" variant="secondary" onClick={() => navigate('/voucher/receive')} data-id="element-1988">
              Cancel
            </Button>
            <Button type="submit" variant="secondary" data-id="element-1989">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-1990" />
              Save as Draft
            </Button>
            <Button type="button" variant="primary" onClick={() => handleSubmit('complete')} data-id="element-1991">
              <CheckIcon className="w-4 h-4 mr-2" data-id="element-1992" />
              Save & Complete
            </Button>
          </div>
        </form>
      </div>

      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title={saveAction === 'draft' ? 'Save as Draft' : 'Save & Complete'} footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-1994">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-1995">
              Confirm
            </Button>
          </>} data-id="element-1993">
        <p className="text-gray-700" data-id="element-1996">
          {saveAction === 'draft' ? 'Save this voucher as draft? You can edit it later.' : 'Complete this voucher? It will be locked for editing and sent for approval.'}
        </p>
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm" data-id="element-1997">
          <div data-id="element-1998">
            <strong data-id="element-1999">Date:</strong> {formData.date}
          </div>
          <div data-id="element-2000">
            <strong data-id="element-2001">Mode:</strong> {formData.transactionMode}
          </div>
          {selectedBankAccount && <>
              <div data-id="element-2002">
                <strong data-id="element-2003">Bank:</strong> {selectedBankAccount.bankName}
              </div>
              <div data-id="element-2004">
                <strong data-id="element-2005">Account:</strong> {selectedBankAccount.accountNo}
              </div>
            </>}
          <div data-id="element-2006">
            <strong data-id="element-2007">Total:</strong> $
            {formData.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0).toLocaleString()}
          </div>
        </div>
      </Modal>
    </div>;
}